import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-create-organization-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule
  ],
  templateUrl: './create-organization-modal.component.html',
  styleUrl: './create-organization-modal.component.scss'
})
export class CreateOrganizationModalComponent implements OnInit, OnDestroy {
  createOrganizationForm!: FormGroup;
  currentStep = 1;
  totalSteps = 3;
  isSubmitting = false;
  isLoadingData = false;
  
  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private http: HttpClient
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Component initialization
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Initialize the reactive form
   */
  private initForm(): void {
    this.createOrganizationForm = this.fb.group({
      // Basic Organization Information
      organizationName: ['', [Validators.required, Validators.minLength(2)]],
      organizationType: ['', [Validators.required]],
      officePhone: ['', [Validators.required]],
      mobilePhone: [''],
      email: ['', [Validators.required, Validators.email]],
      lineId: [''],
      website: [''],
      
      // License Information (รายละเอียดสำหรับออกใบกำกับภาษี)
      taxId: ['', [Validators.required]],
      headOfficeAddress: ['', [Validators.required]],
      projectName: [''],
      taxSoi: [''],
      taxRoad: [''],
      taxSubdistrict: ['', [Validators.required]],
      taxDistrict: ['', [Validators.required]],
      taxProvince: ['', [Validators.required]],
      
      // Admin Rights
      adminRights: ['yes', [Validators.required]]
    });
  }

  /**
   * Navigate to next step
   */
  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      // Validate current step before proceeding
      if (this.validateCurrentStep()) {
        this.currentStep++;
      }
    }
  }

  /**
   * Navigate to previous step
   */
  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  /**
   * Go to specific step
   */
  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      // Only allow going to previous steps or current step
      if (step <= this.currentStep) {
        this.currentStep = step;
      }
    }
  }

  /**
   * Validate current step
   */
  private validateCurrentStep(): boolean {
    // Allow all steps to pass validation for easier testing
    // Only validate format/pattern errors, not required fields
    return true;
  }

  /**
   * Check if field has error
   */
  hasError(fieldName: string, errorType?: string): boolean {
    const control = this.createOrganizationForm.get(fieldName);
    if (!control) return false;
    
    if (errorType) {
      return control.hasError(errorType) && (control.dirty || control.touched);
    }
    
    return control.invalid && (control.dirty || control.touched);
  }

  /**
   * Get error message for field
   */
  getErrorMessage(fieldName: string): string {
    const control = this.createOrganizationForm.get(fieldName);
    if (!control || !control.errors) return '';
    
    if (control.errors['required']) return 'กรุณากรอกข้อมูลนี้';
    if (control.errors['email']) return 'รูปแบบอีเมลไม่ถูกต้อง';
    if (control.errors['minlength']) return `ต้องมีอย่างน้อย ${control.errors['minlength'].requiredLength} ตัวอักษร`;
    if (control.errors['pattern']) {
      switch (fieldName) {
        case 'phoneNumber': return 'หมายเลขโทรศัพท์ต้องเป็นตัวเลข 10 หลัก';
        default: return 'รูปแบบข้อมูลไม่ถูกต้อง';
      }
    }
    
    return 'ข้อมูลไม่ถูกต้อง';
  }

  /**
   * Submit form
   */
  onSubmit(): void {
    if (this.createOrganizationForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      
      const formData = this.createOrganizationForm.value;
      
      // Debug logging - Display complete form data structure
      console.log('='.repeat(80));
      console.log('CREATE ORGANIZATION FORM DATA - DEBUG LOG');
      console.log('='.repeat(80));
      console.log('Raw Form Data:', formData);
      console.log('-'.repeat(80));
      console.log('JSON Structure:');
      console.log(JSON.stringify(formData, null, 2));
      console.log('-'.repeat(80));
      console.log('Form Valid:', this.createOrganizationForm.valid);
      console.log('Form Status:', this.createOrganizationForm.status);
      console.log('='.repeat(80));
      
      // POST request to API
      const apiUrl = `${environment.apiUrl}/companies`;
      console.log('Sending POST request to:', apiUrl);
      
      this.http.post(apiUrl, formData).subscribe({
        next: (response) => {
          console.log('='.repeat(80));
          console.log('API RESPONSE - SUCCESS');
          console.log('='.repeat(80));
          console.log('Response:', response);
          console.log('='.repeat(80));
          
          this.isSubmitting = false;
          alert('สร้างหน่วยงานสำเร็จ!');
          this.activeModal.close({
            success: true,
            organizationData: response
          });
        },
        error: (error) => {
          console.log('='.repeat(80));
          console.log('API RESPONSE - ERROR');
          console.log('='.repeat(80));
          console.error('Error:', error);
          console.log('Error Status:', error.status);
          console.log('Error Message:', error.message);
          if (error.error) {
            console.log('Error Details:', error.error);
          }
          console.log('='.repeat(80));
          
          this.isSubmitting = false;
          alert('เกิดข้อผิดพลาดในการสร้างหน่วยงาน: ' + (error.error?.message || error.message));
        }
      });
    } else {
      // Log validation errors for debugging
      console.log('='.repeat(80));
      console.log('FORM VALIDATION FAILED');
      console.log('='.repeat(80));
      console.log('Form Valid:', this.createOrganizationForm.valid);
      console.log('Form Errors:');
      Object.keys(this.createOrganizationForm.controls).forEach(key => {
        const control = this.createOrganizationForm.get(key);
        if (control && control.errors) {
          console.log(`  ${key}:`, control.errors);
        }
      });
      console.log('='.repeat(80));
      
      // Mark all fields as touched to show validation errors
      Object.keys(this.createOrganizationForm.controls).forEach(key => {
        this.createOrganizationForm.get(key)?.markAsTouched();
      });
    }
  }

  /**
   * Get step title
   */
  getStepTitle(step: number): string {
    switch (step) {
      case 1: return 'ข้อมูลหน่วยงาน';
      case 2: return 'ใบอนุญาต';
      case 3: return 'การจัดการ ADMIN';
      default: return '';
    }
  }

  /**
   * Get step icon
   */
  getStepIcon(step: number): string {
    switch (step) {
      case 1: return 'bi-building';
      case 2: return 'bi-card-checklist';
      case 3: return 'bi-shield-check';
      default: return 'bi-circle';
    }
  }

  /**
   * Check if step is completed
   */
  isStepCompleted(step: number): boolean {
    return step < this.currentStep;
  }

  /**
   * Check if step is current
   */
  isStepCurrent(step: number): boolean {
    return step === this.currentStep;
  }

  /**
   * Get progress percentage
   */
  getProgressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  /**
   * Close modal
   */
  close(): void {
    this.activeModal.close();
  }

  /**
   * Dismiss modal
   */
  dismiss(): void {
    this.activeModal.dismiss();
  }
}
